import { useCallback, useState, type FC } from 'react'
import { Box, type SxProps, type Theme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { DisplayStep } from './DisplayStep.js'
import { type Step } from './model.js'
import { guidedTourClasses } from './classes.js'
import { type FocusBackdropDimensions } from './FocusBackdrop.js'

type Props = {
  documentScrollQuerySelector?: string
  name: string
  onClose?(): void
  steps: Step[]
  sx?: SxProps<Theme>
  onFocusDimensionsChange(dimensions: FocusBackdropDimensions): void
}

export const DisplayTour: FC<Props> = ({
  documentScrollQuerySelector,
  name,
  steps,
  sx,
  onClose,
  onFocusDimensionsChange,
}) => {
  const [currentStep, setCurrentStep] = useState(0)
  const { t } = useTranslation()

  const stepCount = steps.length
  const hasPreviousStep = currentStep > 0
  const hasNextStep = currentStep + 1 < stepCount

  const handleBack = useCallback(() => setCurrentStep(currentStep - 1), [currentStep])
  const handleNext = useCallback(() => setCurrentStep(currentStep + 1), [currentStep])

  const stepProgressText = `${currentStep + 1} ${t('guidedTour.tour.of')} ${stepCount}`

  return (
    <Box className={guidedTourClasses.tour} sx={sx}>
      <DisplayStep
        documentScrollQuerySelector={documentScrollQuerySelector}
        key={`${name}-step-${currentStep}`}
        progress={stepCount > 1 ? stepProgressText : undefined}
        step={steps[currentStep]}
        tourName={name}
        onBack={hasPreviousStep ? handleBack : undefined}
        onClose={onClose}
        onNext={hasNextStep ? handleNext : undefined}
        showCloseIconButton={hasNextStep}
        onFocusDimensionsChange={onFocusDimensionsChange}
      />
    </Box>
  )
}
